<template>
  <div class="secure-image-preview__modal">
    <div class="image-container"
         @contextmenu.prevent
         @selectstart.prevent>
      <!-- Loading state -->
      <div v-if="loading"
           class="image-loading">
        <div class="loading-spinner"></div>
        <p>{{ $gettext('Loading image...') }}</p>
      </div>

      <!-- Error state -->
      <div v-else-if="error"
           class="image-error">
        <p>{{ $gettext('Error loading image') }}</p>
        <button class="sk-btn sk-btn--default"
                @click="loadImage">{{ $gettext('Retry') }}</button>
      </div>

      <!-- Image viewer -->
      <div v-else-if="imageBlobUrl"
           class="image-viewer">
        <!-- Navigation controls -->
        <div class="image-navigation">
          <button class="nav-btn"
                  :title="$gettext('Zoom in (Ctrl/Cmd + +)')"
                  @click="zoomIn">+</button>
          <span class="zoom-info">{{ zoomPercentage }}%</span>
          <button class="nav-btn"
                  :title="$gettext('Zoom out (Ctrl/Cmd + -)')"
                  @click="zoomOut">−</button>
          <button class="nav-btn"
                  :title="$gettext('Reset zoom')"
                  @click="resetZoom">⟲</button>
        </div>

        <!-- Image container with zoom functionality -->
        <div ref="imageContainer"
             class="image-container-inner"
             @contextmenu.prevent
             @selectstart.prevent
             @mousedown="handleMouseDown"
             @mouseup="handleMouseUp"
             @mousemove="handleMouseMove"
             @mouseleave="handleMouseLeave"
             @wheel="handleWheel"
             @touchstart="handleTouchStart"
             @touchmove="handleTouchMove"
             @touchend="handleTouchEnd">
          <div class="image-wrapper"
               :style="imageWrapperStyle"
               @mousedown="startPan">
            <img :src="imageBlobUrl"
                 :alt="data.file.name"
                 class="preview-image"
                 @load="onImageLoad" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {mapState} from 'vuex';

  export default {
    props: {
      data: {
        type: Object,
        default: () => {}
      }
    },
    data() {
      return {
        loading: true,
        error: false,
        imageBlobUrl: null,
        scale: 1.0,
        imageLoaded: false,
        panX: 0,
        panY: 0,
        isPanning: false,
        lastPanX: 0,
        lastPanY: 0
      };
    },
    computed: {
      ...mapState('OneProjectStore', {
        project: ({project}) => project
      }),
      orderId() {
        return this.project?.id;
      },
      attachmentId() {
        // Extract attachment ID from the file link or use file.id if available
        if (this.data.file.id) {
          return this.data.file.id;
        }
        // Fallback: try to extract from link if it contains the ID
        const linkParts = this.data.file.link?.split('/');
        return linkParts?.[linkParts.length - 1];
      },
      imageWrapperStyle() {
        return {
          transform: `translate(${this.panX}px, ${this.panY}px) scale(${this.scale})`,
          transformOrigin: '0 0'
        };
      },
      zoomPercentage() {
        return Math.round(this.scale * 100);
      }
    },
    methods: {
      async loadImage() {
        if (!this.orderId || !this.attachmentId) {
          this.error = true;
          this.loading = false;
          return;
        }

        try {
          this.loading = true;
          this.error = false;

          const blob = await this.$fetcher.downloadAttachment(this.orderId, this.attachmentId);

          if (blob) {
            // Create blob URL for the image
            this.imageBlobUrl = URL.createObjectURL(blob);
            this.loading = false;
          } else {
            throw new Error('No blob data received');
          }
        } catch (error) {
          this.error = true;
          this.loading = false;
        }
      },
      onImageLoad() {
        this.imageLoaded = true;
        this.resetZoom();
      },
      zoomIn() {
        const oldScale = this.scale;
        this.scale = Math.min(this.scale * 1.2, 5.0);
        this.adjustPanForZoom(oldScale, this.scale);
      },
      zoomOut() {
        const oldScale = this.scale;
        this.scale = Math.max(this.scale / 1.2, 0.1);
        this.adjustPanForZoom(oldScale, this.scale);
      },
      resetZoom() {
        this.scale = 1.0;
        this.panX = 0;
        this.panY = 0;
      },
      adjustPanForZoom(oldScale, newScale) {
        // Keep the center of the view centered when zooming
        const scaleRatio = newScale / oldScale;

        // Calculate the center of the current view
        const centerX = -this.panX + (this.$refs.imageContainer?.getBoundingClientRect().width || 0) / 2;
        const centerY = -this.panY + (this.$refs.imageContainer?.getBoundingClientRect().height || 0) / 2;

        // Calculate new pan position to keep the same center point
        this.panX = centerX - (centerX * scaleRatio);
        this.panY = centerY - (centerY * scaleRatio);

        // Ensure the image doesn't go too far out of bounds
        this.constrainPan();
      },
      constrainPan() {
        if (!this.$refs.imageContainer) return;

        const container = this.$refs.imageContainer;
        const containerRect = container.getBoundingClientRect();

        // Calculate bounds based on current scale
        // Allow panning to show the full zoomed image
        const maxPanX = Math.max(0, (this.scale - 1) * containerRect.width);
        const maxPanY = Math.max(0, (this.scale - 1) * containerRect.height);

        this.panX = Math.max(-maxPanX, Math.min(maxPanX, this.panX));
        this.panY = Math.max(-maxPanY, Math.min(maxPanY, this.panY));
      },
      startPan(event) {
        // Handle both mouse and touch events
        if (event.button === 0 || event.type === 'touchstart') { // Left mouse button or touch
          this.isPanning = true;

          if (event.type === 'touchstart') {
            this.lastPanX = event.touches[0].clientX;
            this.lastPanY = event.touches[0].clientY;
          } else {
            this.lastPanX = event.clientX;
            this.lastPanY = event.clientY;
          }

          event.preventDefault();
        }
      },
      handleMouseMove(event) {
        if (this.isPanning && this.scale > 1) {
          const deltaX = event.clientX - this.lastPanX;
          const deltaY = event.clientY - this.lastPanY;

          this.panX += deltaX;
          this.panY += deltaY;

          this.lastPanX = event.clientX;
          this.lastPanY = event.clientY;

          this.constrainPan();
        }
      },
      handleMouseDown(event) {
        // Prevent right mouse button
        if (event.button === 2) {
          event.preventDefault();
          return false;
        }
      },
      handleMouseUp(event) {
        // Prevent right mouse button
        if (event.button === 2) {
          event.preventDefault();
          return false;
        }

        // Stop panning
        this.isPanning = false;
      },
      handleMouseLeave() {
        this.isPanning = false;
      },
      handleWheel(event) {
        // Prevent default scroll behavior
        event.preventDefault();

        // Zoom in/out with mouse wheel + Ctrl/Cmd
        if (event.ctrlKey || event.metaKey) {
          const oldScale = this.scale;

          if (event.deltaY < 0) {
            this.zoomIn();
          } else {
            this.zoomOut();
          }

          // Adjust pan to zoom towards mouse position
          if (this.scale !== oldScale) {
            this.zoomTowardsPoint(event.clientX, event.clientY, oldScale, this.scale);
          }
        }
      },
      zoomTowardsPoint(mouseX, mouseY, oldScale, newScale) {
        if (!this.$refs.imageContainer) return;

        const container = this.$refs.imageContainer;
        const containerRect = container.getBoundingClientRect();

        // Calculate mouse position relative to container center
        const centerX = containerRect.width / 2;
        const centerY = containerRect.height / 2;
        const mouseOffsetX = mouseX - containerRect.left - centerX;
        const mouseOffsetY = mouseY - containerRect.top - centerY;

        // Calculate the zoom point relative to the current pan
        const zoomPointX = (mouseOffsetX - this.panX) / oldScale;
        const zoomPointY = (mouseOffsetY - this.panY) / oldScale;

        // Adjust pan to keep the zoom point under the mouse
        this.panX = mouseOffsetX - zoomPointX * newScale;
        this.panY = mouseOffsetY - zoomPointY * newScale;

        this.constrainPan();
      },
      handleTouchStart(event) {
        if (event.touches.length === 1) {
          this.startPan(event.touches[0]);
        }
      },
      handleTouchMove(event) {
        if (event.touches.length === 1 && this.isPanning && this.scale > 1) {
          const deltaX = event.touches[0].clientX - this.lastPanX;
          const deltaY = event.touches[0].clientY - this.lastPanY;

          this.panX += deltaX;
          this.panY += deltaY;

          this.lastPanX = event.touches[0].clientX;
          this.lastPanY = event.touches[0].clientY;

          this.constrainPan();
        }
      },
      handleTouchEnd() {
        this.isPanning = false;
      },
      disableKeyboardShortcuts() {
        // Disable common keyboard shortcuts for save/download
        document.addEventListener('keydown', (e) => {
          // Ctrl+S, Ctrl+Shift+S, F12, Ctrl+Shift+I, Ctrl+U
          if ((e.ctrlKey && (e.key === 's' || e.key === 'S' || e.key === 'u' || e.key === 'U'))
            || (e.ctrlKey && e.shiftKey && (e.key === 'i' || e.key === 'I'))
            || e.key === 'F12') {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }

          // Add image zoom keyboard shortcuts
          if (this.imageLoaded && !this.loading && !this.error) {
            switch (e.key) {
              case '+':
              case '=':
                if (e.ctrlKey || e.metaKey) {
                  e.preventDefault();
                  this.zoomIn();
                }
                break;
              case '-':
                if (e.ctrlKey || e.metaKey) {
                  e.preventDefault();
                  this.zoomOut();
                }
                break;
              case '0':
                if (e.ctrlKey || e.metaKey) {
                  e.preventDefault();
                  this.resetZoom();
                }
                break;
            }
          }
        }, true);
      },
      overrideModalWidth() {
        // Find the modal wrapper and override its width
        const modalWrapper = document.querySelector('.sk-modal__wrapper');
        if (modalWrapper) {
          modalWrapper.style.setProperty('width', 'auto', 'important');
          modalWrapper.style.setProperty('max-width', '90vw', 'important');
        }
      }
    },
    mounted() {
      // Wait for the next tick to ensure the DOM is fully rendered
      this.$nextTick(() => {
        // Load the image when component is mounted
        this.loadImage();

        // Override the modal wrapper width to remove the 600px constraint
        this.overrideModalWidth();
      });

      // Additional security measures
      this.disableKeyboardShortcuts();
    },
    beforeDestroy() {
      // Clean up blob URL to prevent memory leaks
      if (this.imageBlobUrl) {
        URL.revokeObjectURL(this.imageBlobUrl);
      }
    }
  };
</script>

<style scoped>
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Override the hardcoded 600px width from sk-modal wrapper */
.secure-image-preview__modal {
  position: relative;
  display: flex;
  flex-direction: column;
  width: auto !important;
  width: 100%;
  max-width: 90vw !important;
  height: auto;
  margin-top: -15px;
  padding: 45px 30px 20px;
}

.image-container {
  position: relative;
  overflow: hidden;
  width: 100%;
  min-height: 400px;
  border: 1px solid #ddd;
  border-radius: 4px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.image-navigation {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  flex-shrink: 0;
  gap: 10px;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 10px;
  border-bottom: 1px solid #ddd;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(5px);
}

.nav-btn {
  min-width: 40px;
  margin: 0 !important;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  font-weight: bold;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.nav-btn:hover:not(:disabled) {
  border-color: #999;
  background: #f5f5f5;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.nav-btn:active:not(:disabled) {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  transform: translateY(0);
}

.nav-btn:disabled {
  box-shadow: none;
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.zoom-info {
  min-width: 60px;
  margin: 0 15px;
  color: #333;
  font-weight: 500;
  font-size: 14px;
  text-align: center;
}

.image-container p {
  padding: 20px;
  color: #666;
  text-align: center;
}

.image-loading,
.image-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  padding: 20px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  margin-bottom: 20px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.image-error p {
  margin-bottom: 20px;
  color: #e74c3c;
}

.image-viewer {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

.image-container-inner {
  position: relative;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  width: 100%;
  min-height: 400px;
  padding: 20px;
  cursor: grab;
  touch-action: none; /* Prevent default touch behaviors on mobile */
}

.image-container-inner:active {
  cursor: grabbing;
}

.image-wrapper {
  position: relative;
  display: inline-block;
  transition: transform 0.1s ease;
}

.preview-image {
  display: block;
  object-fit: contain;
  max-width: 100%;
  max-height: 100%;
  pointer-events: none;
}

@media (max-width: 768px) {
  .image-navigation {
    gap: 8px;
    padding: 8px;
  }

  .nav-btn {
    min-width: 36px;
    padding: 6px 10px;
    font-size: 14px;
  }

  .zoom-info {
    min-width: 50px;
    margin: 0 10px;
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .image-navigation {
    gap: 6px;
    padding: 6px;
  }

  .nav-btn {
    min-width: 32px;
    padding: 5px 8px;
    font-size: 13px;
  }

  .zoom-info {
    min-width: 45px;
    margin: 0 8px;
    font-size: 12px;
  }
}
</style>
